import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { MobileBottomBar } from "@/components/mobile/MobileBottomBar"
import type { MobileTopNavProps } from "@/components/mobile/MobileTopNav"
import { MobileTopNav } from "@/components/mobile/MobileTopNav"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useGlobalChatNotifications } from "@/features/chats/hooks/useGlobalChatNotifications"
import { TourResumeModal } from "@/features/tour/components/TourResumeModal"
import { getAllTourStatus, type TourData, updateTour } from "@/features/tour/services/tour.service"
import { useUIStore } from "@/store/uiStore"
import { AppHeader } from "./AppHeader"
import { BackgroundLayer } from "./components/BackgroundLayer"
import { LayoutModals } from "./components/LayoutModals"
import { useDetailPageData } from "./hooks/useDetailPageData"
import { useInlineActions } from "./hooks/useInlineActions"
import { useLayoutState } from "./hooks/useLayoutState"
import { useMoreActions } from "./hooks/useMoreActions"
import { useRouteType } from "./hooks/useRouteType"

interface AppLayoutProps {
  children?: React.ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAuthStore(state => state.user)
  const hideMobileNav = useUIStore(s => s.hideMobileNav)
  const [activeTour, setActiveTour] = useState<TourData | null>(null)

  const handleResumeTour = useCallback(() => {
    if (!activeTour) return
    const step = activeTour.currentScreen
    const tourType = activeTour.tourType
    const ritualId = activeTour.metadata?.ritualId as string | undefined

    setActiveTour(null)

    // Use replace to clear history so completing/skipping tour won't return here
    if (tourType === "ritual_tour" && ritualId) {
      navigate(`/rituals/${ritualId}?tour=ritual&step=${step}`, { replace: true })
    } else if (tourType === "ritual_tour") {
      import("@/features/tour/services/tour.service").then(({ getDemoRitualId }) => {
        getDemoRitualId()
          .then(demoId => {
            if (demoId) {
              navigate(`/rituals/${demoId}?tour=ritual&step=${step}`, { replace: true })
            } else {
              navigate("/", { replace: true })
            }
          })
          .catch(() => navigate("/", { replace: true }))
      })
    } else {
      navigate(`/?tour=home&step=${step}`, { replace: true })
    }
  }, [activeTour, navigate])

  const handleSkipTour = useCallback(async () => {
    if (!activeTour) return
    await updateTour(activeTour.id, { action: "skip" }).catch(() => {})
    setActiveTour(null)
    // Replace history so user can't go back to tour/ritual pages
    navigate("/", { replace: true })
  }, [activeTour, navigate])

  useGlobalChatNotifications()

  const routeType = useRouteType()

  // Track whether we've already detected a tour param on this mount so we
  // don't re-fetch after ritualDetail.tsx clears the ?tour=ritual param.
  const hasSeenTourParamRef = useRef(false)
  const prevIsRitualDetailRef = useRef(false)
  useEffect(() => {
    // Only reset the guard when transitioning away from ritualDetail,
    // not on every mount/remount (which would defeat StrictMode guards).
    if (prevIsRitualDetailRef.current && !routeType.isRitualDetail) {
      hasSeenTourParamRef.current = false
    }
    prevIsRitualDetailRef.current = routeType.isRitualDetail
  }, [routeType.isRitualDetail])

  // Stable ref for location.search so the resume-check callback doesn't go stale
  const locationSearchRef = useRef(location.search)
  locationSearchRef.current = location.search

  const checkForResumeTour = useCallback(() => {
    const tourParam = new URLSearchParams(locationSearchRef.current).get("tour")
    if (tourParam) {
      hasSeenTourParamRef.current = true
      return
    }
    if (hasSeenTourParamRef.current) return
    // Don't fetch if a page is already managing a tour via useTourInit
    if ((window as unknown as Record<string, unknown>).__tourActive) return
    // Set guard synchronously so StrictMode double-invoke is blocked immediately
    hasSeenTourParamRef.current = true
    getAllTourStatus()
      .then(({ home_tour: home, ritual_tour: ritual }) => {
        const tour = home.active || ritual.active
        if (tour && (tour.status === "started" || tour.status === "in_progress")) {
          setActiveTour(tour)
        }
      })
      .catch(() => {
        // Reset guard on error so a remount can retry
        hasSeenTourParamRef.current = false
      })
  }, [])

  // Check for active tours on mount and when location.search changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: location.search is an intentional trigger
  useEffect(() => {
    checkForResumeTour()
  }, [checkForResumeTour, location.search])

  // Re-check when useTourInit discovers an existing active tour and clears __tourActive
  useEffect(() => {
    const handler = () => {
      checkForResumeTour()
    }
    window.addEventListener("rituality:resumeCheck", handler)
    return () => window.removeEventListener("rituality:resumeCheck", handler)
  }, [checkForResumeTour])
  const layoutState = useLayoutState()

  const detailPageData = useDetailPageData({
    isCommunityDetail: routeType.isCommunityDetail,
    isRitualDetail: routeType.isRitualDetail,
    isOtherProfilePage: routeType.isOtherProfilePage,
    communityId: routeType.communityId,
    ritualId: routeType.ritualId,
    otherProfileUserId: routeType.otherProfileUserId,
  })

  const { moreActions } = useMoreActions({
    currentPath: routeType.currentPath,
    isMainNav: routeType.isMainNav,
    isSettingsRoute: routeType.isSettingsRoute,
    isCommunityDetail: routeType.isCommunityDetail,
    isOtherProfilePage: routeType.isOtherProfilePage,
    isRitualDetail: routeType.isRitualDetail,
    ritualUIData: detailPageData.ritualUIData,
    ritualId: routeType.ritualId,
    userId: user?.id,
    communityData: detailPageData.communityData,
    communityId: routeType.communityId,
  })

  const { inlineActions } = useInlineActions({
    isMainNav: routeType.isMainNav,
    isSettingsRoute: routeType.isSettingsRoute,
    isEditProfilePage: routeType.isEditProfilePage,
    isRitualDetail: routeType.isRitualDetail,
    isCommunityDetail: routeType.isCommunityDetail,
    isProfilePage: routeType.isProfilePage,
    isOtherProfilePage: routeType.isOtherProfilePage,
    ritualId: routeType.ritualId,
    ritualUIData: detailPageData.ritualUIData,
    communityId: routeType.communityId,
    communityData: detailPageData.communityData,
    setShareOpen: layoutState.setShareOpen,
  })

  const communityCenterLabel = useMemo<MobileTopNavProps["centerLabel"]>(() => {
    if (!routeType.isCommunityDetail || !detailPageData.communityData?.createdAt) return undefined
    const createdAt = detailPageData.communityData.createdAt as string | Date
    const created = new Date(createdAt)
    const now = new Date()
    const hoursDiff = (now.getTime() - created.getTime()) / (1000 * 60 * 60)
    if (hoursDiff > 24) return undefined
    return (
      <span className="bg-brand-olive border border-brand-olive text-white uppercase px-4 flex items-center py-0 h-[26px] rounded-full text-[10px] font-monteserat font-semibold whitespace-nowrap">
        Just Launched!
      </span>
    )
  }, [routeType.isCommunityDetail, detailPageData.communityData?.createdAt])

  const shareUrl = useMemo(() => {
    if (routeType.isProfilePage && user?.id) {
      return `/profile/${user.slug || user.id}`
    }
    if (routeType.isOtherProfilePage) {
      const userId = routeType.currentPath.split("/")[2]
      return `/profile/${userId}`
    }
    if (routeType.isMyRitualityRoute && user?.id) {
      return `/profile/${user.slug || user.id}`
    }
    return routeType.currentPath
  }, [
    routeType.isProfilePage,
    routeType.isOtherProfilePage,
    routeType.isMyRitualityRoute,
    routeType.currentPath,
    user?.id,
    user?.slug,
  ])

  const shareImageUrl = useMemo(() => {
    if (routeType.isRitualDetail && detailPageData.ritualUIData?.media) {
      return detailPageData.ritualUIData.media.find(m => m.url)?.url || null
    }
    if (routeType.isCommunityDetail && detailPageData.communityData?.media) {
      const cover = detailPageData.communityData.media.find(m => m.isCover)
      return cover?.filePath || detailPageData.communityData.media[0]?.filePath || null
    }
    if (routeType.isOtherProfilePage && detailPageData.otherProfileData?.user?.profilePictureUrl) {
      return detailPageData.otherProfileData.user.profilePictureUrl
    }
    return null
  }, [
    routeType.isRitualDetail,
    routeType.isCommunityDetail,
    routeType.isOtherProfilePage,
    detailPageData,
  ])

  return (
    <div className="relative min-h-screen">
      <div className="mx-auto">
        <BackgroundLayer
          showBackground={routeType.showBackground}
          isDashboardRoute={routeType.isDashboardRoute}
          isAltRoute={routeType.isAltRoute}
          isCreateOrEditRoute={routeType.isCreateOrEditRoute}
          backgroundImage={routeType.backgroundImage}
        />

        <div className="hidden md:block">
          <AppHeader
            onSearchOpen={() => layoutState.handleSearchOpen(true)}
            onNotificationsOpen={layoutState.handleNotificationsOpen}
            onCreateOpen={() => layoutState.handleCreateOpen(true)}
          />
        </div>

        {!routeType.isChatsRoute && !hideMobileNav && (
          <MobileTopNav
            variant={
              routeType.isMyRitualityRoute
                ? "my-rituality"
                : !routeType.isMainNav
                  ? "back-more"
                  : "main"
            }
            onBack={
              !routeType.isMainNav && !routeType.isMyRitualityRoute ? () => navigate(-1) : undefined
            }
            onSearch={() => layoutState.handleSearchOpen(true)}
            onNotifications={() => layoutState.handleNotificationsOpen(true)}
            onHamburgerClick={() => layoutState.setSidebarOpen(true)}
            onShare={() => layoutState.setShareOpen(true)}
            moreActions={moreActions}
            inlineActions={inlineActions}
            centerLabel={communityCenterLabel}
            helpCenterAction={
              !routeType.isMyRitualityRoute && !routeType.isMainNav
                ? () => navigate("/settings?section=help-center")
                : undefined
            }
          />
        )}

        <div
          className={`pt-[20px] md:pt-[70px] md:pb-0 ${
            routeType.isCreateOrEditRoute || routeType.isSettingsRoute ? "pb-0" : "pb-[85px]"
          }`}
        >
          {children || <Outlet />}
        </div>

        {!hideMobileNav &&
          !routeType.isEditProfilePage &&
          !routeType.isCreateOrEditRoute &&
          !routeType.isSettingsRoute && (
            <div className="md:hidden">
              <MobileBottomBar />
            </div>
          )}

        <LayoutModals
          notificationsOpen={layoutState.notificationsOpen}
          searchOpen={layoutState.searchOpen}
          createOpen={layoutState.createOpen}
          shareOpen={layoutState.shareOpen}
          sidebarOpen={layoutState.sidebarOpen}
          reportModalOpen={layoutState.reportModalOpen}
          reportUserId={layoutState.reportUserId}
          reportCommunityModalOpen={layoutState.reportCommunityModalOpen}
          reportRitualModalOpen={layoutState.reportRitualModalOpen}
          handleNotificationsOpen={layoutState.handleNotificationsOpen}
          handleSearchOpen={layoutState.handleSearchOpen}
          handleCreateOpen={layoutState.handleCreateOpen}
          setShareOpen={layoutState.setShareOpen}
          setSidebarOpen={layoutState.setSidebarOpen}
          setReportModalOpen={layoutState.setReportModalOpen}
          setReportCommunityModalOpen={layoutState.setReportCommunityModalOpen}
          setReportRitualModalOpen={layoutState.setReportRitualModalOpen}
          shareUrl={shareUrl}
          shareImageUrl={shareImageUrl}
          shareType={
            routeType.isRitualDetail
              ? "ritual"
              : routeType.isCommunityDetail
                ? "community"
                : "profile"
          }
          isCommunityDetail={routeType.isCommunityDetail}
          isRitualDetail={routeType.isRitualDetail}
          isEditProfilePage={routeType.isEditProfilePage}
          communityData={detailPageData.communityData}
          ritualUIData={detailPageData.ritualUIData}
          otherProfileData={detailPageData.otherProfileData}
        />

        {/* Tour Resume Modal */}
        {activeTour && (
          <TourResumeModal
            open={!!activeTour}
            onResume={handleResumeTour}
            onSkip={handleSkipTour}
          />
        )}
      </div>
    </div>
  )
}
